/**
 * Lingui runtime + locale activation helpers.
 *
 * The app's i18n instance is created here and shared across the app via
 * <I18nProvider> in main.tsx. Locale activation is lazy — message catalogs
 * are dynamic-imported to keep the initial bundle small.
 *
 * Resolution order on first load (used by `resolveInitialLocale`):
 *   1. localStorage["lexicanon:ui-locale"] — explicit user / company pick
 *      persisted on this device.
 *   2. navigator.language — browser preference, matched against SUPPORTED.
 *   3. "en" — fallback.
 *
 * After auth, the signed-in user's `ui_locale` (GET/PUT /api/users/me/ui-locale)
 * wins and is written to localStorage so the next pre-paint matches.
 *
 * Company `default_ui_locale` is applied AFTER initial activation, only when
 * the user has no localStorage choice. Boot must NOT persist the browser
 * fallback — that used to mark every first visit as "user picked" and
 * blocked the company default forever.
 *
 * Calling `activateLocale(code, { persist: true })` updates the i18n
 * instance, optionally persists to localStorage, and updates `<html lang>`.
 */

import { isUiLocale, type TUiLocale, UI_LOCALES } from "@lexicanon/shared"
import { i18n } from "@lingui/core"

export const SUPPORTED_LOCALES = UI_LOCALES
export type TLocale = TUiLocale

export const LOCALE_LABELS: Record<TLocale, string> = {
  en: "English",
  nl: "Nederlands",
}

export const STORAGE_KEY = "lexicanon:ui-locale"

export function isSupportedLocale(value: string): value is TLocale {
  return isUiLocale(value)
}

/**
 * True when this device has an explicit UI locale (user pick, account
 * hydrate, or company default that we persisted). Browser-detected
 * English on first visit must NOT count.
 */
export function hasUserPickedLocale(): boolean {
  if (typeof window === "undefined") return false
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    return saved !== null && isSupportedLocale(saved)
  } catch {
    return false
  }
}

export function resolveInitialLocale(): TLocale {
  if (typeof window === "undefined") return "en"
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved && isSupportedLocale(saved)) return saved
  } catch {
    // localStorage may throw in some sandboxed contexts — fall through
  }
  // navigator.language is e.g. "nl-NL" or "en-US"; take the prefix.
  const browser = (navigator.language ?? "en").split("-")[0].toLowerCase()
  if (isSupportedLocale(browser)) return browser
  return "en"
}

export async function activateLocale(locale: TLocale, options?: { persist?: boolean }): Promise<void> {
  const { messages } = await import(`../locales/${locale}/messages.po`)
  i18n.load(locale, messages)
  i18n.activate(locale)
  const persist = options?.persist ?? true
  if (persist) {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // ignore — non-fatal
    }
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale
  }
}

export { i18n }
